import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Siren, CalendarDays, Clock, Loader2, CheckCircle2, CalendarPlus } from "lucide-react";
import api from "@/lib/api";
import { CONTACT } from "@/data/site";

const input = "w-full bg-[#0d0d0d] border border-white/10 px-4 py-3.5 text-[#F5F5F0] placeholder:text-[#5a5a5a] focus:outline-none focus:border-gold transition-colors duration-300";
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function nextDays(count) {
  const out = [];
  const d = new Date();
  while (out.length < count) {
    out.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}
const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function Booking() {
  const days = nextDays(12);
  const [date, setDate] = useState(fmt(days[0]));
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", address: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [booked, setBooked] = useState(null);
  const [consent, setConsent] = useState(false);
  const up = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const loadSlots = useCallback(async (d) => {
    setLoadingSlots(true); setSlot("");
    try {
      const { data } = await api.get(`/availability?date=${d}`);
      setSlots(data.slots);
    } catch (e) { setSlots([]); }
    finally { setLoadingSlots(false); }
  }, []);

  useEffect(() => { loadSlots(date); }, [date, loadSlots]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) { toast.error("Please add your name, phone and email."); return; }
    if (!slot) { toast.error("Please pick a time slot."); return; }
    if (!consent) { toast.error("Please agree to receive text messages to continue."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/appointments", { ...form, date, slot });
      setBooked(data);
      toast.success("Appointment booked! Check your email for confirmation.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not book. Please call us.");
      loadSlots(date);
    } finally { setSubmitting(false); }
  };

  return (
    <section id="book" className="relative py-24 lg:py-32 border-t border-white/10" data-testid="booking-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-14 max-w-2xl">
          <span className="font-mono text-xs tracking-[0.3em] uppercase text-gold">Schedule a Visit</span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-tight">
            Book your on-site<br /><span className="text-gradient-gold">assessment.</span>
          </h2>
        </div>

        {/* Emergency banner */}
        <div className="mb-10 border border-red-500/30 bg-red-500/[0.06] p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="emergency-banner">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center bg-red-500/15 text-red-400"><Siren className="h-5 w-5" /></span>
            <p className="text-[#F5F5F0]"><span className="font-semibold text-red-400">Active emergency?</span> Don't wait for an appointment — we respond 24/7.</p>
          </div>
          <a href={CONTACT.phoneHref} className="inline-flex items-center gap-2 bg-red-500 text-white font-semibold px-6 py-3 hover:bg-red-400 transition-colors whitespace-nowrap" data-testid="emergency-call-btn">
            <Phone className="h-4 w-4" /> Call 24/7 — {CONTACT.phone}
          </a>
        </div>

        {booked ? (
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
            className="border border-gold/30 bg-[#0d0d0d] p-10 lg:p-14 text-center" data-testid="booking-success">
            <CheckCircle2 className="h-16 w-16 text-gold mx-auto" />
            <h3 className="font-display text-3xl mt-6">You're on the schedule</h3>
            <p className="text-[#A3A3A3] mt-3">{booked.date} · {booked.slot_label}</p>
            <p className="text-[#A3A3A3] mt-1 max-w-md mx-auto">A confirmation was emailed to you. Add it to your iPhone or Apple Calendar below.</p>
            <div className="mt-8 flex flex-wrap gap-4 justify-center">
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/appointments/${booked.id}/calendar.ics`}
                className="inline-flex items-center gap-2 bg-gold text-black font-semibold px-7 py-3.5 hover:bg-gold-light transition-colors" data-testid="add-to-calendar-btn">
                <CalendarPlus className="h-5 w-5" /> Add to Apple / iPhone Calendar
              </a>
              <button onClick={() => { setBooked(null); loadSlots(date); }} className="border border-gold text-gold px-7 py-3.5 hover:bg-gold hover:text-black transition-colors" data-testid="book-another-btn">
                Book Another
              </button>
            </div>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Left: date + slots */}
            <div>
              <p className="font-mono text-[11px] tracking-widest uppercase text-[#A3A3A3] mb-3 flex items-center gap-2"><CalendarDays className="h-4 w-4 text-gold" /> Choose a day</p>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="day-picker">
                {days.map((d) => {
                  const v = fmt(d); const active = v === date;
                  return (
                    <button key={v} onClick={() => setDate(v)} data-testid={`day-${v}`}
                      className="flex flex-col items-center py-3 border transition-colors"
                      style={active ? { background: "#D4AF37", color: "#050505", borderColor: "#D4AF37" } : { borderColor: "rgba(255,255,255,0.1)", color: "#F5F5F0" }}>
                      <span className="text-[10px] font-mono uppercase opacity-70">{DOW[d.getDay()]}</span>
                      <span className="text-lg font-display leading-none mt-1">{d.getDate()}</span>
                      <span className="text-[10px] font-mono uppercase opacity-70">{MON[d.getMonth()]}</span>
                    </button>
                  );
                })}
              </div>

              <p className="font-mono text-[11px] tracking-widest uppercase text-[#A3A3A3] mt-8 mb-3 flex items-center gap-2"><Clock className="h-4 w-4 text-gold" /> Pick a time</p>
              {loadingSlots ? (
                <div className="py-6 text-[#A3A3A3]"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="slot-picker">
                  {slots.map((s) => (
                    <button key={s.start} disabled={!s.available} onClick={() => setSlot(s.start)} data-testid={`slot-${s.start}`}
                      className="px-4 py-3 border text-sm text-left transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      style={slot === s.start ? { background: "#D4AF37", color: "#050505", borderColor: "#D4AF37" } : { borderColor: "rgba(255,255,255,0.1)", color: "#F5F5F0" }}>
                      {s.label}{!s.available && " · booked"}
                    </button>
                  ))}
                  {slots.every((s) => !s.available) && <p className="text-sm text-[#A3A3A3] col-span-full">No openings this day — try another.</p>}
                </div>
              )}
            </div>

            {/* Right: contact form */}
            <form onSubmit={submit} className="border border-white/10 bg-[#0d0d0d] p-6 lg:p-8 space-y-4 self-start" data-testid="booking-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <input className={input} placeholder="Full name *" value={form.name} onChange={up("name")} data-testid="book-name" />
                <input className={input} placeholder="Phone *" value={form.phone} onChange={up("phone")} data-testid="book-phone" />
              </div>
              <input type="email" className={input} placeholder="Email *" value={form.email} onChange={up("email")} data-testid="book-email" />
              <input className={input} placeholder="Service (water, fire, mold, roof…)" value={form.service} onChange={up("service")} data-testid="book-service" />
              <input className={input} placeholder="Property address" value={form.address} onChange={up("address")} data-testid="book-address" />
              <textarea rows={3} className={`${input} resize-none`} placeholder="Anything we should know?" value={form.message} onChange={up("message")} data-testid="book-message" />
              <div className="text-sm text-[#A3A3A3] border border-white/10 px-4 py-3 bg-black/30">
                Selected: <span className="text-gold">{date}</span>{slot ? <> · <span className="text-gold">{slots.find((s) => s.start === slot)?.label}</span></> : " · pick a time"}
              </div>
              <label className="flex items-start gap-3 cursor-pointer select-none" data-testid="booking-consent-label">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                  className="mt-1 h-4 w-4 shrink-0 accent-[#D4AF37] cursor-pointer" data-testid="booking-consent-checkbox" />
                <span className="text-xs text-[#8a8a8a] leading-relaxed" data-testid="booking-sms-consent">
                  I agree to receive SMS text messages from 247 Aftermath Restoration Mgmt about my appointment (confirmations and reminders) at the number provided. Message frequency varies. Message &amp; data rates may apply. Reply STOP to opt out, HELP for help. See our <a href="/privacy" className="text-gold underline hover:text-gold-light">Privacy Policy</a> &amp; <a href="/terms" className="text-gold underline hover:text-gold-light">Terms</a>.
                </span>
              </label>
              <button type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 bg-gold text-black font-semibold px-8 py-4 hover:bg-gold-light transition-colors disabled:opacity-60" data-testid="book-submit">
                {submitting ? <><Loader2 className="h-5 w-5 animate-spin" /> Booking…</> : <><CalendarDays className="h-5 w-5" /> Confirm Appointment</>}
              </button>
            </form>
          </div>
        )}
      </div>
    </section>
  );
}
